// shared/notifications.ts
// Decides which reminders may be sent, and refuses far more often than it
// agrees.
//
// The research behind plans/caloly-notifications.md is blunt: reminders and
// streak mechanics in calorie apps are named as design features that worsen
// eating-disorder risk, and 46% of users switch notifications off entirely
// after two to five irrelevant messages in a week. Suppression is therefore
// both the kind thing and the effective thing, which is why the engine exists
// before any new reminder type does.
//
// The rules it enforces structurally, so no caller can forget one:
//
//  1. Master switch off → nothing.
//  2. A reminder whose task is already done is never sent. Not deferred — not
//     sent. This is the whole point.
//  3. Nothing inside quiet hours.
//  4. Never more than MAX_PER_DAY, counting what has already gone out today.
//     When more qualify, the earliest wins and the rest are dropped rather
//     than queued — a backlog delivered at 21:00 is exactly the pile-up the
//     cap exists to prevent.
//  5. Nothing whose time has already passed.
//
// It says nothing about copy. Wording lives in the locale files, and the
// constraint there (never mention streaks, days missed, what was eaten, or a
// target exceeded) is a separate rule this module cannot enforce.
import type { MealSlot } from "./types";

export const REMINDER_KINDS = [
  "weigh_in", "meal_lunch", "meal_dinner", "water", "day_wrap", "check_in_ready",
  // Positive-only encouragement (owner request). Copy must be true whether
  // or not anything was logged — the mascot rules ban every guilt shape.
  "encourage",
] as const;
export type ReminderKind = (typeof REMINDER_KINDS)[number];

export function isReminderKind(s: string): s is ReminderKind {
  return (REMINDER_KINDS as readonly string[]).includes(s);
}

/** Two. Not a starting point to tune upward — see the opt-out numbers above. */
export const MAX_PER_DAY = 2;

export const DEFAULT_TIMES: Record<ReminderKind, string> = {
  weigh_in: "08:00",
  meal_lunch: "13:30",
  meal_dinner: "20:00",
  water: "15:00",
  day_wrap: "21:30",
  // Event-driven: it goes out when the check-in becomes available, not on a
  // clock. The time is only a floor for "not before".
  check_in_ready: "09:00",
  encourage: "18:30",
};

/**
 * Everything off until asked for. Notification permission is requested
 * in-context, at the moment a toggle is switched on, so a kind that defaults to
 * enabled would be a stored claim of consent nobody gave.
 *
 * Breakfast is absent from the kinds entirely, not merely off: the morning
 * already has weigh_in, and two notifications before 9 AM is how people turn
 * all of them off.
 */
export const DEFAULT_ENABLED: Record<ReminderKind, boolean> = {
  weigh_in: false,
  meal_lunch: false,
  meal_dinner: false,
  water: false,
  day_wrap: false,
  check_in_ready: false,
  encourage: false,
};

/** What flips ON when the person says yes to the first-launch permission ask
 *  (Onboarding's final step). Explicit consent at that moment is what the
 *  defaults-off principle above demands — this list is the "yes" they gave. */
export const ACCEPT_DEFAULTS: ReminderKind[] = ["meal_lunch", "meal_dinner", "water", "encourage"];

export const DEFAULT_QUIET_FROM = "22:00";
export const DEFAULT_QUIET_TO = "07:00";

export interface ReminderPrefs {
  master: boolean;
  quiet_from: string; // "HH:MM"
  quiet_to: string;   // "HH:MM"
  kinds: Partial<Record<ReminderKind, { enabled: boolean; local_time: string }>>;
}

/** What the user has already done today. Everything the engine needs to decide
 *  "this is done, say nothing". */
export interface DayState {
  weight_logged: boolean;
  /** Slots with at least one entry. */
  slots_logged: MealSlot[];
  water_ml: number;
  water_target_ml: number;
  /** Any food entry at all — day_wrap must not nag someone who never opened
   *  the app today. */
  any_entry: boolean;
  check_in_ready: boolean;
}

export interface PlannedReminder {
  kind: ReminderKind;
  /** Minutes since local midnight. */
  at: number;
}

export function parseHhMm(s: string): number | null {
  const m = /^(\d{2}):(\d{2})$/.exec(s);
  if (!m) return null;
  const h = Number(m[1]), min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
}

export function formatHhMm(minutes: number): string {
  const m = ((minutes % 1440) + 1440) % 1440;
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
}

/** Quiet hours normally wrap midnight (22:00–07:00), so this is not a simple
 *  range check. from === to means the whole day is quiet. */
export function inQuietHours(minutes: number, from: string, to: string): boolean {
  const f = parseHhMm(from), t = parseHhMm(to);
  if (f === null || t === null) return false; // unreadable prefs must not silence everything
  if (f === t) return true;
  return f < t ? minutes >= f && minutes < t : minutes >= f || minutes < t;
}

/** True when this reminder's reason to exist has already happened. */
export function alreadyDone(kind: ReminderKind, s: DayState): boolean {
  switch (kind) {
    // Encouragement is never "already done" — the copy is written to be true
    // on any kind of day, so the engine's caps and quiet hours are its only
    // gatekeepers.
    case "encourage": return false;
    case "weigh_in":       return s.weight_logged;
    case "meal_lunch":     return s.slots_logged.includes("lunch");
    case "meal_dinner":    return s.slots_logged.includes("dinner");
    case "water":          return s.water_target_ml <= 0 || s.water_ml >= s.water_target_ml;
    // Nothing logged at all is not an incomplete day, it is a day off. Every
    // slot filled is a complete one. Both mean silence.
    case "day_wrap":       return !s.any_entry || (["breakfast", "lunch", "dinner"] as MealSlot[])
                                    .every((slot) => s.slots_logged.includes(slot));
    case "check_in_ready": return !s.check_in_ready;
  }
}

export interface PlanInput {
  /** Minutes since midnight in the user's own time zone. */
  nowMinutes: number;
  prefs: ReminderPrefs;
  state: DayState;
  /** Kinds already delivered today, however that was recorded. */
  sentToday: ReminderKind[];
}

/**
 * The reminders that may still be delivered today, earliest first.
 *
 * Call it whenever the day's state could have changed — on foreground and
 * after a write — and reschedule from the result. A local notification cannot
 * know you logged lunch ten minutes ago; recomputing at those moments is what
 * makes rule 2 true in practice rather than only on paper.
 */
export function planReminders({ nowMinutes, prefs, state, sentToday }: PlanInput): PlannedReminder[] {
  const candidates: PlannedReminder[] = [];
  for (const { kind, configured } of eligible(prefs, state, sentToday)) {
    // The check-in is an event: it becomes true at some point during the day
    // and should not wait for tomorrow's slot. Anything else keeps its clock.
    const at = kind === "check_in_ready" ? Math.max(configured, nowMinutes) : configured;

    if (at < nowMinutes) continue;              // its moment has passed
    if (inQuietHours(at, prefs.quiet_from, prefs.quiet_to)) continue;
    candidates.push({ kind, at });
  }

  // Earliest first, then cut to the remaining budget. Dropped, not queued.
  candidates.sort((a, b) => a.at - b.at || REMINDER_KINDS.indexOf(a.kind) - REMINDER_KINDS.indexOf(b.kind));
  return candidates.slice(0, remainingBudget(prefs, sentToday));
}

/**
 * The same decision, asked the other way round: what may go out *in this tick*.
 *
 * On-device scheduling can plan the whole day ahead because the device will
 * still be there at 20:00. A cron cannot — it wakes every few minutes and has
 * exactly one chance to send. So this selects kinds whose moment has just
 * passed, within `windowMin`, rather than kinds still ahead.
 *
 * Every suppression rule is shared with planReminders through `eligible`, which
 * is the point: two delivery paths must never disagree about whether a reminder
 * is allowed, only about when they get to ask.
 */
export function dueNow(
  { nowMinutes, prefs, state, sentToday }: PlanInput,
  windowMin = 15,
): PlannedReminder[] {
  // Quiet hours are judged against now, because now is when it would arrive.
  if (inQuietHours(nowMinutes, prefs.quiet_from, prefs.quiet_to)) return [];

  const due: PlannedReminder[] = [];
  for (const { kind, configured } of eligible(prefs, state, sentToday)) {
    // A ready check-in is due from its earliest time onward — it has no slot to
    // miss, and waiting a whole day to mention it would be absurd.
    const inWindow = kind === "check_in_ready"
      ? nowMinutes >= configured
      : nowMinutes >= configured && nowMinutes < configured + windowMin;
    if (inWindow) due.push({ kind, at: configured });
  }

  due.sort((a, b) => a.at - b.at || REMINDER_KINDS.indexOf(a.kind) - REMINDER_KINDS.indexOf(b.kind));
  return due.slice(0, remainingBudget(prefs, sentToday));
}

function remainingBudget(prefs: ReminderPrefs, sentToday: ReminderKind[]): number {
  if (!prefs.master) return 0;
  return Math.max(0, MAX_PER_DAY - sentToday.length);
}

/** Every rule that does not depend on the clock: the master switch, whether the
 *  kind is switched on, whether it already went out today, and — the one that
 *  matters most — whether its reason to exist has already happened. */
function eligible(
  prefs: ReminderPrefs, state: DayState, sentToday: ReminderKind[],
): { kind: ReminderKind; configured: number }[] {
  if (!prefs.master) return [];
  const out: { kind: ReminderKind; configured: number }[] = [];
  for (const kind of REMINDER_KINDS) {
    if (sentToday.includes(kind)) continue;
    const pref = prefs.kinds[kind];
    if (!(pref?.enabled ?? DEFAULT_ENABLED[kind])) continue;
    if (alreadyDone(kind, state)) continue;
    const configured = parseHhMm(pref?.local_time ?? DEFAULT_TIMES[kind]);
    if (configured === null) continue;
    out.push({ kind, configured });
  }
  return out;
}
